/**
 * Permission grouping for the role editor.
 *
 * The DB `permissions.category` column gives us a rough bucket but it's
 * too coarse for the role editor (everything `invoicing.*` collapses to
 * one giant 50-row group, "ai" lumps the chatbot, agents, and workflows
 * together, etc.). This module fans the codes out into user-friendly
 * feature groups so the role editor can render collapsible sections
 * like "Bank Statements", "AI Chatbot", "AI Agents", "Workflows",
 * "Accounts Receivable", "Accounts Payable", "Budget Planning", etc.
 *
 * Each entry returns the FIRST matching group via prefix-longest-wins,
 * so more specific prefixes (`invoicing.bills.*`) win over broader
 * ones (`invoicing.*`).
 */

export type PermissionGroup = {
  /** Stable id used in expand/collapse state. */
  id: string
  /** User-facing label rendered as the group header. */
  label: string
  /** One-line helper text shown under the label. */
  description?: string
  /**
   * Code prefixes (or exact codes) that belong in this group, evaluated
   * longest-first so more specific prefixes win over broader ones.
   */
  match: string[]
}

// Order = display order in the role editor.
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Financial dashboard and KPI tiles.',
    match: ['dashboard.'],
  },
  {
    id: 'statements',
    label: 'Bank Statements',
    description: 'Upload, view, classify and manage bank statement transactions.',
    match: ['statements.'],
  },
  {
    id: 'banking',
    label: 'Bank Feeds (Plaid)',
    description: 'Connect external bank accounts and refresh feeds.',
    match: ['banking.'],
  },
  {
    id: 'accounting_core',
    label: 'Accounting - Core',
    description: 'Journal entries, chart of accounts, reconciliation, year-end close.',
    match: [
      'accounting.journal_entries',
      'accounting.coa',
      'accounting.reconcile',
      'accounting.year_end_close',
      'accounting.tb_import',
      'accounting.schedules',
      'accounting.allocations',
      'accounting.accruals',
    ],
  },
  {
    id: 'accounting_reporting',
    label: 'Financial Reports',
    description: 'Generate and configure income statement, balance sheet, cash flow and trial balance.',
    match: ['accounting.reporting', 'accounting.reporting_config'],
  },
  {
    id: 'budget',
    label: 'Budget Planning',
    description: 'Create and manage budgets and variance reporting.',
    match: ['accounting.budget'],
  },
  {
    id: 'ar_customers',
    label: 'AR - Customers',
    description: 'Manage customer master data and customer portal.',
    match: ['invoicing.customers.', 'invoicing.customer_portal.'],
  },
  {
    id: 'ar_invoices',
    label: 'AR - Invoices',
    description: 'Draft, post, send, void invoices and recurring schedules.',
    match: [
      'invoicing.invoices.',
      'invoicing.recurring.',
      'invoicing.reminders.',
      'invoicing.ar.aging.',
    ],
  },
  {
    id: 'ar_payments',
    label: 'AR - Payments & Credits',
    description: 'Record payments, credit notes, refunds and write-offs against AR invoices.',
    match: [
      'invoicing.payments.record',
      'invoicing.credits.',
      'invoicing.refunds.',
      'invoicing.write_offs.',
      'invoicing.payouts.',
    ],
  },
  {
    id: 'ap_vendors',
    label: 'AP - Vendors',
    description: 'Manage vendor master data and vendor bank accounts.',
    match: ['invoicing.vendors.'],
  },
  {
    id: 'ap_bills',
    label: 'AP - Bills',
    description: 'Draft, post, approve, void and ingest vendor bills.',
    match: [
      'invoicing.bills.',
      'invoicing.exceptions.',
      'invoicing.ai.suggestions.',
      'invoicing.ap.aging.',
    ],
  },
  {
    id: 'ap_payments',
    label: 'AP - Payments',
    description: 'Schedule, release, cancel and remit payments to vendors.',
    match: [
      'invoicing.payments.schedule',
      'invoicing.payments.release',
      'invoicing.payments.cancel',
      'invoicing.payment_batches.',
      'invoicing.remittance.',
    ],
  },
  {
    id: 'invoicing_ai',
    label: 'Invoicing AI',
    description: 'AI auto-approval, draft collections, anomaly detection, cash forecasts.',
    match: ['invoicing.ai.'],
  },
  {
    id: 'invoicing_controls',
    label: 'Invoicing Enterprise Controls',
    description: 'Segregation of duties, audit export, multi-entity, tax configuration.',
    match: [
      'invoicing.sod.',
      'invoicing.audit.',
      'invoicing.multi_entity.',
      'invoicing.tax.',
    ],
  },
  {
    id: 'approvals',
    label: 'Approvals',
    description: 'View, act on, delegate, and configure approval policies.',
    match: ['approvals.'],
  },
  {
    id: 'chatbot',
    label: 'AI Chatbot',
    description: 'Use the AI CFO chatbot and share chats with the workspace.',
    match: ['ai.chatbot', 'chatbot.'],
  },
  {
    id: 'agents',
    label: 'AI Agents',
    description: 'Create and manage tenant-scoped AI agents.',
    match: ['agents.'],
  },
  {
    id: 'workflows',
    label: 'Workflows',
    description: 'Create, manage and trigger automation workflows.',
    match: ['workflows.'],
  },
  {
    id: 'client',
    label: 'Client Intake',
    description: 'Manage client onboarding and intake forms.',
    match: ['client.'],
  },
  {
    id: 'team',
    label: 'Team Management',
    description: 'Invite members, assign roles, manage custom roles.',
    match: ['team.'],
  },
  {
    id: 'api_keys',
    label: 'API Keys',
    description: 'Issue and revoke programmatic API keys for the workspace.',
    match: ['api_keys.'],
  },
  {
    id: 'audit',
    label: 'Audit Log',
    description: 'View and purge the workspace audit log.',
    match: ['audit.'],
  },
  {
    id: 'data',
    label: 'Data Export',
    description: 'Export workspace data for backup or migration.',
    match: ['data.'],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description: 'View in-app notifications.',
    match: ['notifications.'],
  },
  {
    id: 'profile',
    label: 'Profile',
    description: 'View and edit personal profile.',
    match: ['profile.'],
  },
  {
    id: 'tenant_settings',
    label: 'Workspace Settings',
    description: 'Edit business details, regional settings and session timeout.',
    match: ['tenant.', 'workspace.'],
  },
  {
    id: 'billing',
    label: 'Subscription & Billing',
    description: 'View and manage the workspace subscription and invoices.',
    match: ['billing.'],
  },
  {
    id: 'security',
    label: 'Security',
    description: 'Manage 2FA and personal session security.',
    match: ['security.'],
  },
  {
    id: 'support',
    label: 'Support',
    description: 'Contact Bokapsys sales and support.',
    match: ['support.'],
  },
]

// Last-resort bucket for codes nothing else claims (defensive default so a
// permission added in a migration without updating this file still appears).
const UNGROUPED: PermissionGroup = {
  id: 'other',
  label: 'Other',
  description: 'Permissions not yet categorised.',
  match: [],
}

function matchScore(code: string, prefix: string): number {
  // Longest-wins: an exact match on `agents.manage` beats the `agents.`
  // bucket. Returns the prefix length so the caller can pick the highest.
  if (prefix === code) return prefix.length + 100
  if (prefix.endsWith('.') && code.startsWith(prefix)) return prefix.length
  if (!prefix.endsWith('.') && code === prefix) return prefix.length + 100
  return 0
}

export function groupForCode(code: string): PermissionGroup {
  let best: { group: PermissionGroup; score: number } | null = null
  for (const g of PERMISSION_GROUPS) {
    for (const p of g.match) {
      const score = matchScore(code, p)
      if (score > 0 && (!best || score > best.score)) {
        best = { group: g, score }
      }
    }
  }
  return best ? best.group : UNGROUPED
}

export type GroupedPermissions = Array<{
  group: PermissionGroup
  permissions: Array<{ code: string; description: string | null }>
}>

export function groupPermissions(
  flat: ReadonlyArray<{ code: string; description: string | null }>,
): GroupedPermissions {
  const buckets = new Map<string, { group: PermissionGroup; permissions: typeof flat[number][] }>()
  for (const p of flat) {
    const g = groupForCode(p.code)
    if (!buckets.has(g.id)) buckets.set(g.id, { group: g, permissions: [] })
    buckets.get(g.id)!.permissions.push(p)
  }
  // Preserve PERMISSION_GROUPS display order; "Other" trails everything.
  const ordered: GroupedPermissions = []
  for (const g of PERMISSION_GROUPS) {
    const bucket = buckets.get(g.id)
    if (bucket && bucket.permissions.length > 0) {
      ordered.push({
        group: g,
        permissions: [...bucket.permissions].sort((a, b) => a.code.localeCompare(b.code)),
      })
    }
  }
  const other = buckets.get(UNGROUPED.id)
  if (other && other.permissions.length > 0) {
    ordered.push({
      group: UNGROUPED,
      permissions: [...other.permissions].sort((a, b) => a.code.localeCompare(b.code)),
    })
  }
  return ordered
}
